import { assertNotNull } from '@/helpers/typing'
import { DocsView, DocSortOption, filteredDocsByDocType } from './doc'
import { ListDescriptionView } from './docs.list.description'
import { ArticlesView } from './doc.article'
import { BooksView } from './doc.book'
import { CollectionsView } from './docs.list.collection'
import { PodcastsView } from './docs.list.podcasts'
import {
  ListDescriptionViewPodcast,
  PodcastEpisodesView,
} from './docs.list.podcasts.episodes'
import {
  ApiResultDocs,
  DocType,
  CollectionListItem,
  ListItem,
  ListItemType,
  CustomList,
} from './interfaces'

import { backend } from '@/services/backend'
import { loadBookListFromNativeStorage } from '@/services/native.storage'
import { offline } from '@/services/offline'
import { capitalizeTagFirstLetter, truncateSearchTerm } from '@/helpers/text'
import { docTypeLabel } from '@/models/doc'

/**
 * A minimal interface for the DocsView-like data classes
 * that can be displayed with a ListView.
 */
export interface DocsListView {
  docTypeName(): ListItemType
  get filteredDocs(): ListItem[]
  get dataLength(): number
  setShowLimit(limit: number): void
  isDocSortVisible(): boolean
}

/**
 * Listing view.
 *
 * Wrapper around the DocsView that provides additional settings and
 * logic for the ListPage component.
 *
 * The ListView represents the list page with this structure:
 *
 *   [ page title                        ]
 *
 *   [ Description (ListDescriptionView) ]
 *
 *   [ Title             ] [ filter box  ]
 *
 *   [ The list of documents (DocsView)  ]
 *   [ renderd with ListCard component   ]
 *
 * We have separate classes for the list of documents (DocsView) and
 * the description (ListDescriptionView) that vary depending on
 * the document type.
 *
 * The ListView is used in the ListPage component. The component is unified
 * and can be used for all document types (books, articles, podcasts, etc).
 *
 * At the same time, we have a way to display list items (documents) in a
 * different way. This is done by the ListCard component that uses the
 * dynamic ":is" attribute to render the component based on the document
 * type (for example, we have BookCard for books and ArticleCard for articles).
 *
 * In the case we add a new document type, we need to add a new card component
 * for it and add the mapping in the ListCard component.
 *
 * In pages where we need a different card than the default card for the given
 * document type, we can pass the component name of this different card to
 * `options.customCardComponent`. This card component also has to be mapped
 * in ListCard.
 *
 * To add a new listing page, we need to add a new route to the routes.ts
 * file and update the `createListViewByRoute` function below to create
 * the ListView with the correct DocsView and ListDescriptionView.
 *
 * Routes for ListView look like this:
 *
 * ```
 * {
 *   path: '/app/books/category/:category',
 *   name: 'bookCategory',
 *   component: ListPage,
 *   meta: {
 *     keepAlive: true,
 *     componentName: 'ListPage',
 *   },
 * }
 * ```
 *
 * And the approprite code in `createListViewByRoute` function:
 *
 * ```
 * if (routeName === 'bookCategory') {
 *   const tag = decodeURI(params.category as string)
 *   const title = capitalizeTagFirstLetter(tag)
 *   const docsView = await loadBooks(tag)
 *   return new ListView(title, docsView)
 * }
 * ```
 *
 * The description view is optional and used for the top area of the list page.
 * For example for podcast with the list of episodes we have a podcast
 * description at the top with the image and "listen to original" link and
 * then we have the list of episodes below.
 *
 * We also have description view for collections, in the code it looks like this:
 *
 * ```
 * if (routeName === 'bookCollection') {
 *   const urlSlug = params.collection as string
 *   const { docsView, collection } = await loadCollectionBooks(urlSlug)
 *   const descriptionView = new ListDescriptionView(
 *     collection,
 *     collection.doc_count,
 *   )
 *   return new ListView(descriptionView.title, docsView, descriptionView)
 * }
 * ```
 *
 * Above we load the collection and create the `DocsView` for it, then aslo
 * create the `ListDescriptionView` and pass it to the ListView that manages
 * the page UI.
 */
export class ListView {
  private _title: string
  private _filterTitle: string

  private _docs: DocsListView
  private _descriptionView?: ListDescriptionView
  private _customList?: CustomList

  private _viewAllLink?: string
  private _viewAllText?: string

  private _customCardComponent?: string

  private _isHeaderCollapsed: boolean = false

  public constructor(
    title: string,
    docs: DocsListView,
    options?: {
      descriptionView?: ListDescriptionView
      customList?: CustomList
      filterTitle?: string
      viewAllLink?: string
      viewAllText?: string
      customCardComponent?: string
    },
  ) {
    this._title = title
    this._docs = docs
    this._descriptionView = options?.descriptionView
    this._customList = options?.customList
    this._filterTitle = options?.filterTitle || this._title
    this._viewAllLink = options?.viewAllLink
    this._viewAllText = options?.viewAllText
    this._customCardComponent = options?.customCardComponent
  }

  public pageTitle(): string {
    return this._title
  }

  public filterTitle(): string {
    return this._filterTitle
  }

  public docTypeName(): ListItemType {
    return this.docs().docTypeName()
  }

  public notFoundMessage(): string {
    const docTypesLabel = docTypeLabel[this.docTypeName()].plural
    return `No ${docTypesLabel} found.`
  }

  public docs(): DocsListView {
    return assertNotNull(this._docs)
  }

  public get dataLength(): number {
    return this.docs().dataLength
  }

  public hasDocs(): boolean {
    if (this._docs && this._docs.dataLength > 0) {
      return true
    }
    return false
  }

  public get customList(): CustomList | undefined {
    return this._customList
  }

  public get descriptionView(): ListDescriptionView | undefined {
    return this._descriptionView
  }

  public get viewAllLink(): string | undefined {
    return this._viewAllLink
  }

  public get viewAllText(): string | undefined {
    return this._viewAllText
  }

  public get customCardComponent(): string | undefined {
    return this._customCardComponent
  }

  /**
   * Get `this.docTypeName()` formatted to be used in CSS classes.
   */
  get cssClassModifier(): string {
    return this.docTypeName().replace(/_/g, '-')
  }

  /**
   * Get header state for listing pages
   */
  get isHeaderCollapsed(): boolean {
    return this._isHeaderCollapsed
  }

  /**
   * Set header state from scroll position
   */
  set isHeaderCollapsed(value: boolean) {
    this._isHeaderCollapsed = value
  }

  setShowLimit(limit: number): void {
    this.docs().setShowLimit(limit)
  }
}

/**
 * Listing page route names for books.
 *
 * How to add a new listing page:
 * - Add the route name to this enum.
 * - Add Listing page configuration to the ``ListingPages`` const.
 * - Add mapping between route name and user-friendly title to the
 *   ``pageTypeToTitleMap`` object.
 */
export enum BookPageType {
  all = 'all',
  popular = 'popular',
  new = 'new',
  favorites = 'favorites',
  reading = 'reading',
  finished = 'finished',
  downloads = 'downloads',
  free = 'free',
  recommended = 'recommended',
  category = 'category',
  search = 'search',
}

export enum ArticlePageType {
  all = 'all',
  favorites = 'favorites',
  viewed = 'viewed',
}

export enum PodcastPageType {
  favorites = 'favorites',
  reading = 'reading',
  finished = 'finished',
}

const userBookPages = [
  BookPageType.favorites,
  BookPageType.downloads,
  BookPageType.reading,
  BookPageType.finished,
]

/**
 * Load books from the backend and create DocsView.
 */
async function loadBooks(tag?: string): Promise<DocsView> {
  if (offline.isOffline) {
    await loadBookListFromNativeStorage()
  }

  const response = await backend.getBooksWithRecommendations()
  const view = await BooksView.createView(response, undefined, true)
  if (tag) {
    view.setTag(tag)
  }
  return view
}

type DocsViewAndCollection = {
  docsView: DocsView
  collection: CollectionListItem
}

type DocsViewAndCustomList = {
  docsView: DocsView
  list: CustomList
}

/**
 * Load books from the backend and create DocsView.
 */
async function loadCollectionBooks(
  collectionUrlSlug: string,
): Promise<DocsViewAndCollection> {
  if (offline.isOffline) {
    await loadBookListFromNativeStorage()
  }

  const response = await backend.getCollectionBooks(collectionUrlSlug)
  return {
    docsView: await BooksView.createView(response, undefined, true),
    collection: response.collection,
  }
}

/**
 * Load books from the backend and create DocsView.
 */
async function loadCustomListDocs(
  listUrlSlug: string,
): Promise<DocsViewAndCustomList> {
  const response = await backend.getCustomListDocs(listUrlSlug)
  assertNotNull(response.data)
  return {
    docsView: await BooksView.createView(response, DocSortOption.none, true),
    list: response.list,
  }
}

/**
 * Create a list view for books based on the page type, get data from the home API.
 */
async function createUserBooksListView(
  pageType: BookPageType,
): Promise<ListView> {
  const response = await backend.getUserBooksWithRecommendations()

  const docsView = await BooksView.createView(response, undefined, true)

  switch (pageType) {
    case BookPageType.favorites:
      docsView.enableActionsButton()
      docsView.setIsFavorite(true)
      return new ListView('Favorites', docsView)

    case BookPageType.reading:
      docsView.enableContinueReading()
      docsView.enableActionsButton()
      docsView.setList('reading')
      docsView.sort(DocSortOption.accessed)
      return new ListView('Reading Now', docsView)

    case BookPageType.finished:
      docsView.enableActionsButton()
      docsView.setList('finished')
      return new ListView('Finished', docsView)

    case BookPageType.downloads:
      await docsView.filterByDownloads()
      return new ListView('Downloads', docsView)
  }
  throw new Error('Unexpected page type: ' + pageType)
}

/**
 * Create a list view for books based on the page type, get data from the books API.
 */
async function createBooksListView(pageType: BookPageType): Promise<ListView> {
  const docsView = await loadBooks()

  switch (pageType) {
    case BookPageType.all:
      return new ListView('All Books', docsView)

    case BookPageType.new:
      docsView.sort(DocSortOption.first_published_at)
      docsView.setDocsLimit(20)
      return new ListView('New Releases', docsView)

    case BookPageType.popular:
      docsView.sort(DocSortOption.popular)
      docsView.setDocsLimit(20)
      return new ListView('Popular', docsView)

    case BookPageType.free:
      docsView.filterByFree()
      return new ListView('Free', docsView)

    case BookPageType.recommended:
      docsView.sort(DocSortOption.recommended)
      docsView.setDocsLimit(20)
      return new ListView('Recommended', docsView)
  }
  throw new Error('Unexpected page type: ' + pageType)
}

async function loadCollections(): Promise<CollectionsView> {
  const collectionsData = await backend.getBookCollections()
  return new CollectionsView(collectionsData)
}

/**
 * Load articles from the backend and create DocsView.
 */
async function loadArticles(tag?: string): Promise<DocsView> {
  const response = await backend.getArticles()
  const view = await ArticlesView.createView(response, true)
  if (tag) {
    view.setTag(tag)
  }
  return view
}

/**
 * Create a list view for articles based on the page type.
 */
async function createArticlesListView(
  pageType: ArticlePageType,
): Promise<ListView> {
  const docsView = await loadArticles()

  switch (pageType) {
    case ArticlePageType.all:
      return new ListView('All Articles', docsView)

    case ArticlePageType.favorites:
      docsView.setIsFavorite(true)
      return new ListView('Favorites', docsView)

    case ArticlePageType.viewed:
      docsView.enableContinueReading()
      docsView.enableActionsButton()
      docsView.setList('viewed')
      docsView.sort(DocSortOption.accessed)
      return new ListView('Viewed', docsView)
  }
  throw new Error('Unexpected page type: ' + pageType)
}

/**
 * Create a list view with search results.
 */
export async function createFollowedPodcastEpisodesView(): Promise<ListView> {
  const showPodcastAsSubtitle = true
  const response = await backend.getFollowedPodcastEpisodes()
  const docsView = new PodcastEpisodesView(
    response,
    showPodcastAsSubtitle,
    DocSortOption.none,
  )
  docsView.setDocsLimit(100)
  return new ListView('Latest Episodes', docsView, {
    viewAllLink: '/app/podcasts/episodes/list/followed',
  })
}

/**
 * Create a list view for podcast episodes based on the page type.
 *
 * This is used for list pages opened from the My Library
 * blocks: favorites, reading now and finished for podcasts tab.
 */
async function createLibraryPodcastEpisodesView(
  pageType: PodcastPageType,
): Promise<ListView> {
  const userDocs = await backend.getUserDocs()
  const episodes = filteredDocsByDocType(userDocs, 'podcast_episode')
  const title = {
    favorites: 'Favorites',
    reading: 'Reading Now',
    finished: 'Finished',
  }[pageType]

  const episodesView = new PodcastEpisodesView(
    episodes.data,
    false,
    DocSortOption.none,
  )
  await episodesView.init()

  episodesView.enableActionsButton()

  if (pageType === PodcastPageType.reading) {
    episodesView.enableContinueReading()
    episodesView.setList('reading')
  }

  if (pageType === PodcastPageType.favorites) {
    episodesView.setIsFavorite(true)
  }

  if (pageType === PodcastPageType.finished) {
    episodesView.setList('finished')
  }

  return new ListView(title, episodesView)
}

/**
 * Load search results for podcast episodes.
 */
async function searchPodcastEpisodes(searchTerm: string): Promise<DocsView> {
  const response = await searchDocsLimited('podcast_episode', searchTerm)
  return PodcastEpisodesView.createView(response, false, DocSortOption.none)
}

/**
 * Load search results for podcasts.
 */
async function searchPodcasts(searchTerm: string): Promise<DocsListView> {
  return PodcastsView.createSearchView(searchTerm)
}

/**
 * A helper function to load search results for books.
 */
async function searchBooks(searchTerm: string): Promise<DocsView> {
  const response = await searchDocsLimited('book', searchTerm)
  return BooksView.createView(response, DocSortOption.none, true)
}

export async function searchArticles(searchTerm: string): Promise<DocsView> {
  const response = await searchDocsLimited('article', searchTerm)
  return ArticlesView.createView(response, true, DocSortOption.none)
}

/**
 * A helper function to load search results.
 */
async function searchDocsLimited(
  docType: DocType,
  searchTerm: string,
): Promise<ApiResultDocs> {
  // We limit the search results to 100 documents.
  // In the future we need to increase this or/and add paging here.
  const limit = 100
  return await backend.getDocSearch(docType, searchTerm, limit)
}

type RouteParams = {
  name?: string
  category?: string
  term?: string
  url_slug?: string
  collection?: string
}

/**
 * Create a listing view based on the route name and parameters.
 */
export async function createListViewByRoute(
  routeName: string,
  params: RouteParams,
): Promise<ListView> {
  if (routeName === 'articleCategory') {
    const tag = decodeURI(params.category as string)
    const title = capitalizeTagFirstLetter(tag)
    const docsView = await loadArticles(tag)
    return new ListView(title, docsView)
  }

  if (routeName === 'bookCategory') {
    const tag = decodeURI(params.category as string)
    const title = capitalizeTagFirstLetter(tag)
    const docsView = await loadBooks(tag)
    return new ListView(title, docsView)
  }

  if (routeName === 'bookSpecialListingPage') {
    const pageType = params.name as BookPageType
    if (userBookPages.includes(pageType)) {
      // Load data from the home API
      return createUserBooksListView(pageType)
    } else {
      // Load data from the books API
      return createBooksListView(pageType)
    }
  }

  if (routeName === 'articleSpecialListingPage') {
    const pageType = params.name as ArticlePageType
    return createArticlesListView(pageType)
  }

  if (routeName === 'bookSearchPage') {
    const term = params.term as string
    const title = `Search results for "${truncateSearchTerm(term)}"`
    const docsView = await searchBooks(term)
    return new ListView(title, docsView)
  }

  if (routeName === 'articleSearchPage') {
    const term = params.term as string
    const title = `Search results for "${truncateSearchTerm(term)}"`
    const docsView = await searchArticles(term)
    return new ListView(title, docsView)
  }

  if (routeName === 'podcast_episodes.search') {
    const term = params.term as string
    const title = `Search results for "${truncateSearchTerm(term)}"`
    const docsView = await searchPodcastEpisodes(term)
    return new ListView(title, docsView)
  }

  if (routeName === 'podcast_episodes.followed') {
    return await createFollowedPodcastEpisodesView()
  }

  if (routeName === 'episodesLibraryListingPage') {
    const pageType = params.name as PodcastPageType
    return await createLibraryPodcastEpisodesView(pageType)
  }

  if (routeName === 'podcast.search') {
    const term = params.term as string
    const title = `Search results for "${truncateSearchTerm(term)}"`
    const docsView = await searchPodcasts(term)
    return new ListView(title, docsView)
  }

  if (routeName === 'podcastPage') {
    const podcastUrlSlug = params.url_slug as string
    const podcast = await backend.getPodcast(podcastUrlSlug)

    const descriptionView = new ListDescriptionViewPodcast(
      { ...podcast, created: '', doc_type: 'podcast' },
      podcast.docs.data.length.toString(),
    )
    const docsView = new PodcastEpisodesView(
      podcast.docs.data,
      false, // Show podcast as subtitle
      DocSortOption.none,
    )
    docsView.enableContinueReading()
    await docsView.init()
    const view = new ListView(podcast.title, docsView, {
      descriptionView,
      filterTitle: 'Episodes',
    })
    return view
  }

  if (routeName === 'bookCollection') {
    const urlSlug = params.collection as string
    const { docsView, collection } = await loadCollectionBooks(urlSlug)
    const descriptionView = new ListDescriptionView(
      collection,
      collection.doc_count,
    )
    return new ListView(descriptionView.title, docsView, { descriptionView })
  }

  if (routeName === 'collections') {
    const title = 'All Collections'
    const docsView = await loadCollections()
    return new ListView(title, docsView)
  }

  if (routeName === 'customList') {
    const url_slug = assertNotNull(params.url_slug)

    const { docsView, list } = await loadCustomListDocs(url_slug)

    docsView.setCustomList(url_slug)
    docsView.enableActionsButton()

    return new ListView(list.title, docsView, { customList: list })
  }

  throw new Error('Unexpected route name: ' + routeName)
}
